import { useCallback, useRef, useState } from 'react';
import {
  dependabilityV2API,
  LocationFlag,
} from '@/services/dependabilityV2API';

/**
 * Loads a worker's location flags (GRAVY-10365).
 *
 * The endpoint returns the whole window in one response, so there is no
 * pagination here: `totalCount`, `unresolvedCount` and `windowDays` are the
 * server's own numbers rather than anything derived from `flags.length`. That
 * is what keeps the badge, the modal header and the list from disagreeing —
 * the AC's controlling rule.
 *
 * `error` is kept distinct from "empty" so a failed fetch offers a retry rather
 * than rendering as "no flags", which would read as a clean worker.
 */
const useLocationFlags = (workerId: number) => {
  const [flags, setFlags] = useState<LocationFlag[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  const [windowDays, setWindowDays] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  // Latest-wins: a slow first response must not paint over a newer one.
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    setLoading(true);
    setError(false);
    try {
      const res = await dependabilityV2API.getLocationFlags(workerId);
      if (requestRef.current !== requestId) return;

      setFlags(res.data.flags);
      setTotalCount(res.data.total_count);
      setUnresolvedCount(res.data.unresolved_count);
      setWindowDays(res.data.window_days);
      setLoaded(true);
    } catch {
      if (requestRef.current !== requestId) return;
      setError(true);
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  }, [workerId]);

  return {
    flags,
    totalCount,
    unresolvedCount,
    windowDays,
    loading,
    loaded,
    error,
    load,
    retry: load,
  };
};

export default useLocationFlags;
